/**
 * Schedule route: planning view for ScopeBars on a job/group × time grid (load, create,
 * resize, drag between rows/days). Wires {@link useScheduleContext} into layout, modals,
 * and {@link ScheduleTimelineBody}.
 */
import ScheduleScopeModal from "../../features/planning/schedule/dialogs/ScheduleScopeModal";
import ScheduleCreateGroupModal from "../../features/planning/schedule/dialogs/ScheduleCreateGroupModal";
import ScheduleDeleteScopeBlockModal from "../../features/planning/schedule/dialogs/ScheduleDeleteScopeBlockModal";
import ScheduleJoinScopeBlocksModal from "../../features/planning/schedule/dialogs/ScheduleJoinScopeBlocksModal";
import ScheduleDispatchEditedWarningModal from "../../features/planning/schedule/dialogs/ScheduleDispatchEditedWarningModal";
import { SCHEDULE_LEFT_PANEL_WIDTH } from "../../features/planning/schedule/constants";
import {
  BG_PRIMARY_1,
  BG_SURFACE,
  BORDER_PRIMARY_3,
  BORDER_SURFACE,
} from "../../features/planning/schedule/style";
import ScheduleTimelineBody from "../../features/planning/schedule/ScheduleTimelineBody";
import TimelineHeader from "../../features/planning/schedule/timeline/TimelineHeader";
import { Alert } from "../../components/ui";
import { ConfirmModal } from "../../components/ui";
import WorkerSelectModal from "../../features/planning/shared/WorkerSelectModal";
import CrewCard from "../../features/planning/shared/CrewCard";
import WorkerAssignmentDiffBanner from "../../features/planning/schedule/dialogs/WorkerAssignmentDiffBanner";
import { getAssignmentDifference } from "../../features/planning/schedule/model/scheduleWorkerAssignmentDiff";
import {
  buildMapByNumericId,
  pickItemsByNumericIdsInOrder,
} from "../../utils/collections/numericId";
import {
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
} from "../../utils/datetime/core";
import { useScheduleContext } from "../../context/domain/planning/ScheduleContext";

/**
 * Schedule page route. Owns route-level chrome and modals; timeline layers read grid
 * state and actions from {@link useScheduleContext} (no props).
 *
 * @returns {JSX.Element}
 */
const Schedule = () => {
  const {
    workScopes,
    zoomLevel,
    setZoomLevel,
    showWeekends,
    timelineStart,
    scheduleTreeFullyExpanded,
    scheduleExpandCollapseAllDisabled,
    toggleExpandCollapseAllScheduleProjectsAndGroups,
    setTimelineLayout,
    horizontalScrollLeft,
    setHorizontalScrollLeft,
    createModalOpen,
    createDefaultWorkingDaysOnly,
    scopeSettingsModalOpen,
    scopeSettingsBlock,
    createGroupModalOpen,
    handleCloseCreateGroupModal,
    handleSaveScheduleGroup,
    moveTimeline,
    resetTimelineToToday,
    handleCloseCreateModal,
    handleCreateScope,
    closeScopeSettingsModal,
    handleConfirmScopeSettings,
    workerDragConflictModal,
    confirmWorkerDragConflictModal,
    cancelWorkerDragConflictModal,
    scopeBlockJoinModal,
    confirmScopeBlockJoinModal,
    cancelScopeBlockJoinModal,
    dispatchEditedWarningModal,
    confirmDispatchEditedWarningModal,
    cancelDispatchEditedWarningModal,
    workerUnskipConflictModal,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
    aspectWorkersModal,
    scheduleWorkerModalUsedWorkers,
    closeAspectWorkersModal,
    submitAspectWorkersModal,
    workers,
    blockDraftWorkers,
    isScheduleDayLocked,
    unlockScheduleDay,
    planningLastUpdated,
  } = useScheduleContext();

  return (
    <div className={`min-h-screen ${BG_PRIMARY_1} px-4 py-4`}>
      <div className={`mx-auto w-full min-w-[800px] max-w-[1600px] rounded-lg border ${BORDER_PRIMARY_3} ${BG_SURFACE} p-4 px-0`}>
        <div className="mx-4 mb-3 flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-gray-900">R&S Planning Schedule</h1>
          <div className="truncate text-right text-xs text-gray-600">
            {planningLastUpdated}
          </div>
        </div>

        <div className={`overflow-hidden rounded-lg border ${BORDER_SURFACE } ${BG_SURFACE} shadow-sm`}>
          <TimelineHeader
            className="rounded-none border-0"
            startDate={timelineStart}
            zoomLevel={zoomLevel}
            showWeekends={showWeekends}
            leftPanelWidth={SCHEDULE_LEFT_PANEL_WIDTH}
            onPrev={() => moveTimeline(-1)}
            onNext={() => moveTimeline(1)}
            onToday={resetTimelineToToday}
            onZoomChange={setZoomLevel}
            scheduleTreeFullyExpanded={scheduleTreeFullyExpanded}
            scheduleExpandCollapseAllDisabled={scheduleExpandCollapseAllDisabled}
            onToggleScheduleExpandCollapseAll={
              toggleExpandCollapseAllScheduleProjectsAndGroups
            }
            onLayoutChange={setTimelineLayout}
            horizontalScrollLeft={horizontalScrollLeft}
            onHorizontalScroll={setHorizontalScrollLeft}
            isScheduleDayLocked={isScheduleDayLocked}
            onUnlockScheduleDay={unlockScheduleDay}
          />
          <ScheduleTimelineBody />
        </div>

        <ScheduleDeleteScopeBlockModal
          open={Boolean(deleteScopeBlockModal)}
          block={deleteScopeBlockModal}
          busy={deleteScopeBlockBusy}
          onClose={closeDeleteScopeBlockModal}
          onConfirm={confirmDeleteScopeBlock}
        />

        <ScheduleJoinScopeBlocksModal
          open={scopeBlockJoinModal != null}
          onClose={cancelScopeBlockJoinModal}
          onConfirm={confirmScopeBlockJoinModal}
        />

        <ScheduleDispatchEditedWarningModal
          open={dispatchEditedWarningModal != null}
          deleteWarning={Boolean(dispatchEditedWarningModal?.deleteWarning)}
          onConfirm={confirmDispatchEditedWarningModal}
          onClose={cancelDispatchEditedWarningModal}
        />

        <ConfirmModal
          isOpen={workerDragConflictModal != null}
          title="Worker Conflict Detected"
          buttonText="Confirm"
          overlayClassName="z-[10070]"
          onConfirm={confirmWorkerDragConflictModal}
          onClose={cancelWorkerDragConflictModal}
        >
          <div className="mx-auto max-w-md space-y-2 text-left">
            {(workerDragConflictModal ?? []).map(({ dateKey, conflictNames }) => (
              <Alert
                key={dateKey}
                type="warning"
                message={`${formatShortMonthDay(
                  getDateInLocalTZFromDateStr(dateKey)
                )}: ${conflictNames.join(", ")}`}
              />
            ))}
            <div className="text-gray-600 pt-2 text-center">
              Days with worker conflicts in this scope will be assigned override crews
              without the conflicting workers.
            </div>
          </div>
        </ConfirmModal>

        <ConfirmModal
          isOpen={workerUnskipConflictModal != null}
          title="Worker Conflict Detected"
          buttonText="Confirm"
          overlayClassName="z-[10070]"
          onConfirm={confirmWorkerUnskipConflictModal}
          onClose={cancelWorkerUnskipConflictModal}
        >
          <div className="mx-auto max-w-md space-y-2 text-left">
            {(workerUnskipConflictModal ?? []).map(({ dateKey, conflictNames }) => (
              <Alert
                key={dateKey}
                type="warning"
                message={`${formatShortMonthDay(
                  getDateInLocalTZFromDateStr(dateKey)
                )}: ${conflictNames.join(", ")}`}
              />
            ))}
            <div className="pt-2 text-center text-gray-600">
              This unskipped day will be assigned an override crew without the
              conflicting workers.
            </div>
          </div>
        </ConfirmModal>

        <ScheduleScopeModal
          open={createModalOpen}
          variant="create"
          title="Add scope"
          defaultWorkingDaysOnly={createDefaultWorkingDaysOnly}
          workScopes={workScopes}
          onClose={handleCloseCreateModal}
          onCreate={handleCreateScope}
        />

        <ScheduleScopeModal
          open={scopeSettingsModalOpen}
          variant="settings"
          title="Scope Settings"
          workScopes={workScopes}
          initialWorkScopeId={scopeSettingsBlock?.scope?.id}
          scheduleBlockId={scopeSettingsBlock?.id}
          onClose={closeScopeSettingsModal}
          onConfirmSettings={handleConfirmScopeSettings}
        />

        <ScheduleCreateGroupModal
          open={createGroupModalOpen}
          onClose={handleCloseCreateGroupModal}
          onSubmit={handleSaveScheduleGroup}
        />

        {aspectWorkersModal ? (
          <WorkerSelectModal
            key={`${aspectWorkersModal.crewEditMode ?? "perDay"}-${aspectWorkersModal.aspectId}`}
            isOpen
            onClose={closeAspectWorkersModal}
            onSubmit={submitAspectWorkersModal}
            date={aspectWorkersModal.day ?? new Date()}
            scope={{
              scope: { title: aspectWorkersModal.block?.scope?.name ?? "" },
              job: {
                number: aspectWorkersModal.block?.job?.number ?? "",
                description: aspectWorkersModal.block?.job?.description ?? "",
              },
            }}
            usedWorkers={scheduleWorkerModalUsedWorkers}
            selectedWorkerIds={aspectWorkersModal.aspectId === `block-draft` ? blockDraftWorkers.current.map((w) => w.id) : aspectWorkersModal.selectedWorkerIds}
            workers={workers ?? undefined}
            headerContent={
              aspectWorkersModal.fromScopeSettings &&
                aspectWorkersModal.crewEditMode === "block" ? (
                <CrewCard
                  workers={aspectWorkersModal.block?.worker_assignment ?? []}
                  title="Current Default Crew"
                  variant="default"
                  readOnly
                />
              ) : null
            }
            footerContent={(selectedIds) => {
              // No comparison for draft crew as it is newly created
              if (aspectWorkersModal.aspectId === `block-draft`) {
                return null;
              }
              const block = aspectWorkersModal.block;
              const list = workers ?? [];
              const byId = buildMapByNumericId(list);
              const syntheticAspect = {
                worker_assignment: pickItemsByNumericIdsInOrder(byId, selectedIds),
              };
              const diff = getAssignmentDifference(block, syntheticAspect, true);
              if (!diff) {
                return null;
              }
              const { removed, added } = diff;
              const editingBlockCrew =
                aspectWorkersModal.crewEditMode === "block";
              return (
                <WorkerAssignmentDiffBanner
                  removed={removed}
                  added={added}
                  heading={
                    editingBlockCrew
                      ? "Changes From Current Assignment"
                      : "Override from planned"
                  }
                  tone={editingBlockCrew ? "neutral" : "override"}
                />
              );
            }}
          />
        ) : null}

      </div>

    </div>
  );
};

export default Schedule;
